import { AlarmClock, CalendarDays, Check, Download, FileQuestion, HelpCircle, Info, LayoutGrid, MessageCircle, Palette, Shield, Sparkles, UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import AccountRow from "../../firebase/AccountRow";
import PremiumRow from "../../firebase/PremiumRow";
import { usePremium, isNativeAndroid, guessIsPH, detectCountry } from "../../firebase/billing";
import FaqList from "../../components/FaqList";
import { appFaqs } from "../../constants/appFaqs";
import { premiumFaqs } from "../../firebase/premiumFaqs";
import { APP_THEME_OPTIONS } from "../../theme/colorEngine";
import { PICKER_PRESETS } from "../../constants/palettes";
import Modal from "../../components/Modal";
import ConfirmHeader from "../../components/ConfirmHeader";
import { CONFIRM_STACK, CONFIRM_CANCEL, CONFIRM_PRIMARY, OPTION_SHADOW, optionButton } from "../../theme/sharedStyles";
import { LockBadge } from "../../components/LockBadge";
import { appBlockerIsNative } from "../../capacitor/appBlocker";
import AppBlockerSection from "./AppBlockerSection";
import { SNOOZE_OPTIONS, getStoredSnoozeMinutes, loadSnoozeMinutes, saveSnoozeMinutes, showSnoozeSetting } from "../../capacitor/snoozeSetting";
import { WIDGET_COLOR_OPTIONS, WIDGET_COLOR_THEME, WIDGET_COLOR_WHITE, WIDGET_COLOR_BLACK, loadWidgetColor, saveWidgetColor, showWidgetColorSetting, pastelWidgetTheme } from "../../capacitor/widgetColor";
import { holidayChoice, setHolidayChoice, regionalHolidayCountry, countryName } from "../../utils/holidays";
import { HOLIDAY_COUNTRIES } from "../../constants/holidayCountries";
export function SettingsPanel({ onClose, initialSection = null, onWideChange = () => {}, bgTheme = "none", onThemeSelect = () => {}, darkActive = false, onDarkSelect = () => {}, defaultAccent = "#d4789a", onDefaultAccentSelect = () => {}, tabOptions = [], enabledTabs = {}, onToggleTab = () => {}, gcal = null, onSyncNow = () => {}, onSyncTasksNow = () => {}, habits = [], habitLogs = {}, habitSkips = [], setHabitLogs = () => {}, setHabits = () => {}, tasks = [], setTasks = () => {}, calEvents = [], categories = [], focusTimerState = null, setFocusTimerState = () => {}, setFocusSessions = () => {}, diary = {}, onNewNote = () => {}, onAddNew = () => {}, isDesktop = false, weekStart = "sunday", onWeekStartChange = () => {}, calGridSurface = "match", onCalGridSurfaceChange = () => {}, dayStartHour = 0, onDayStartHourChange = () => {}, canInstall = false, promptInstall = () => {}, onReplayOnboarding = () => {} }) {
  // Opens already expanded to `initialSection` when the panel was launched
  // via a deep link (e.g. Clock-In's "Open Settings" link) instead of the
  // usual collapsed state.
  const [openSetting, setOpenSetting] = useState(initialSection);
  const appBlockerRowRef = useRef(null);
  const [pulseAppBlockerRow, setPulseAppBlockerRow] = useState(false);
  useEffect(() => {
    if (initialSection !== "appBlocker" || !appBlockerRowRef.current) return;
    appBlockerRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    setPulseAppBlockerRow(true);
    const id = setTimeout(() => setPulseAppBlockerRow(false), 1200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Which sub-item inside the "Planner Options" dropdown (Google Calendar /
  // Start of Week / Day Starts At) is expanded — separate from openSetting
  // so nesting them under one parent row doesn't need a two-part key scheme.
  const [plannerSubOpen, setPlannerSubOpen] = useState(null);
  const toggleSub = (key) => setPlannerSubOpen(p => p === key ? null : key);
  const [replayConfirm, setReplayConfirm] = useState(false);
  const { isPremium, loading: premiumLoading } = usePremium();
  const appBlockerLocked = !isPremium && !premiumLoading;
  // Google Calendar sync is Premium: Connect is locked for free accounts, and
  // an existing connection shows "Paused" (App.jsx stops syncing it). Locked
  // state shows a plain indicator, not tappable — see the render below.
  const toggle = (key) => setOpenSetting(p => p === key ? null : key);

  // FAQs: payment questions follow how this person pays (Google Play in the
  // Android app; PayMongo in the Philippines or Lemon Squeezy elsewhere on the
  // web), same choice as the purchase page.
  const [webRail, setWebRail] = useState(() => (guessIsPH() ? "paymongo" : "lemonsqueezy"));
  useEffect(() => {
    if (isNativeAndroid()) return;
    let alive = true;
    detectCountry().then(c => { if (alive && c) setWebRail(c === "PH" ? "paymongo" : "lemonsqueezy"); });
    return () => { alive = false; };
  }, []);
  const paymentFaqs = premiumFaqs(isNativeAndroid() ? "play" : webRail);
  // One answer open at a time across both FAQ groups (Jade).
  const [openFaq, setOpenFaq] = useState(null);

  // Alarm snooze length — see capacitor/snoozeSetting.js.
  const [snoozeMinutes, setSnoozeMinutes] = useState(() => getStoredSnoozeMinutes());
  useEffect(() => {
    let alive = true;
    loadSnoozeMinutes().then(m => { if (alive) setSnoozeMinutes(m); });
    return () => { alive = false; };
  }, []);
  const pickSnooze = (m) => { setSnoozeMinutes(m); saveSnoozeMinutes(m); };

  // Home-screen widget colour — see capacitor/widgetColor.js.
  const [widgetColor, setWidgetColor] = useState(() => loadWidgetColor());
  const pickWidgetColor = (c) => setWidgetColor(saveWidgetColor(c));
  const [widgetHelpOpen, setWidgetHelpOpen] = useState(false);

  // Public holidays on the Planner — see utils/holidays.js. "Regional" follows
  // where the phone is; "Pick a country" uses the one chosen here; "Off" hides them.
  const [holidayPick, setHolidayPick] = useState(() => holidayChoice());
  const regionalCountry = regionalHolidayCountry();
  const holidayCountryOptions = useMemo(
    () => HOLIDAY_COUNTRIES.map(cc => ({ cc, name: countryName(cc) })).sort((a, b) => a.name.localeCompare(b.name)),
    []
  );
  const pickHolidayMode = (mode) => setHolidayPick(setHolidayChoice(mode, holidayPick.manual || regionalCountry || "PH"));
  const pickHolidayCountry = (cc) => setHolidayPick(setHolidayChoice("manual", cc));
  const holidaySummary = holidayPick.mode === "off" ? "Off"
    : holidayPick.mode === "manual" ? countryName(holidayPick.manual)
    : (regionalCountry ? `Regional · ${countryName(regionalCountry)}` : "Regional");

  const saveWeek  = onWeekStartChange;
  const fmtStartHour = (h) => {
    if (h === 0) return "12 AM";
    if (h < 12) return `${h} AM`;
    if (h === 12) return "12 PM";
    return `${h - 12} PM`;
  };

  const rowStyle = { padding: "14px 20px", borderBottom: "1px solid var(--c-settings-line, #f5eaf0)", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 };
  const iconStyle = { fontSize: 18, width: 24, textAlign: "center", flexShrink: 0 };
  const labelStyle = { flex: 1, fontSize: 14, color: "var(--c-text)", fontWeight: 500 };
  const chevStyle = { fontSize: 12, color: "var(--c-text-soft)", transition: "transform 0.2s" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Panel header */}
      <div style={{ padding: "18px 20px 14px", borderBottom: "1.5px solid var(--c-border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--c-gradient)", flexShrink: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* App logo beside the name (Jade, 2026-10-11); same image as the sign-in page. */}
          {/* -3px: centres the 40px logo over the account row's 34px avatar below (both rows pad 20px). */}
          <img src="/app-logo.png" alt="" style={{ width: 40, height: 40, borderRadius: 10, objectFit: "cover", flexShrink: 0, marginLeft: -3 }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text-accent)", letterSpacing: "-0.3px" }}>clarity</span>
        </span>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-mid)", lineHeight: 1 }}><X size={16} /></button>
      </div>

      {/* Rows */}
      <div style={{ flex: 1, overflowY: "auto" }}>
      <>
        {/* Install as app — only shows when the browser can offer its native install prompt */}
        {canInstall && (
          <div onClick={promptInstall} style={rowStyle}>
            <Download size={18} color="var(--c-text-accent)" />
            <span style={labelStyle}>Install App</span>
            <span style={{ fontSize: 11, color: "var(--c-text-soft)", fontWeight: 600 }}>Add to device</span>
          </div>
        )}

        {/* Account (only shows when signed in / Firebase configured) */}
        <AccountRow />

        {/* Premium / paywall (only shows when signed in / Firebase configured) */}
        <PremiumRow />

        {/* Planner Options — Google Calendar, Start of Week, and Day Starts At,
            merged into one dropdown so Settings isn't three separate rows for
            planner-related preferences. */}
        <div onClick={() => toggle("planner")} style={rowStyle}>
          <CalendarDays size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Planner Options</span>
          <span style={{ ...chevStyle, transform: openSetting === "planner" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "planner" && (
        <div style={{ background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>

        {/* Google Calendar — connect & two-way sync (only when configured) */}
        {gcal?.configured && (
          <>
            <div onClick={() => toggleSub("gcal")} style={{ ...rowStyle, background: "var(--c-bg-card)" }}>
              <span style={labelStyle}>Google Calendar</span>
              <span style={{ fontSize: 11, color: gcal.connected && isPremium ? "#4caf50" : "var(--c-text-soft)", fontWeight: 600, marginRight: 4 }}>
                {gcal.connected ? (isPremium ? "Connected" : "Paused") : "Off"}
              </span>
              <span style={{ ...chevStyle, transform: plannerSubOpen === "gcal" ? "rotate(90deg)" : "none" }}>▸</span>
            </div>
            {plannerSubOpen === "gcal" && (
              <div style={{ padding: "10px 20px 16px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
                {gcal.connected && !isPremium ? (
                  <>
                    {/* Still connected from when this account (or another one on
                        this phone) was Premium — sync is paused (App.jsx gates it
                        on isPremium); Disconnect stays available. */}
                    <div style={{ width: "100%", padding: "11px", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: "var(--c-text-soft)", background: "var(--c-bg-card)", border: "1.5px solid var(--c-border)", boxSizing: "border-box" }}>
                      <LockBadge size={18} /> Google Calendar sync is a Premium feature
                    </div>
                    <button onClick={gcal.disconnect}
                      style={{ marginTop: 8, width: "100%", padding: "10px", borderRadius: 11, border: "none", boxShadow: OPTION_SHADOW, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-card)" }}>
                      Disconnect
                    </button>
                  </>
                ) : !gcal.connected ? (
                  <>
                    {/* Locked indicator, not tappable — Google Calendar sync is
                        Premium; the Settings panel's own Upgrade button (in
                        the Premium row above) is the one path to that page. */}
                    {!isPremium ? (
                      <div style={{ width: "100%", padding: "11px", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: "var(--c-text-soft)", background: "var(--c-bg-card)", border: "1.5px solid var(--c-border)" }}>
                        <LockBadge size={18} /> Google Calendar sync is a Premium feature
                      </div>
                    ) : (
                      <button onClick={() => gcal.connect()} disabled={gcal.connecting}
                        style={{ width: "100%", padding: "11px", borderRadius: 12, border: "none", cursor: gcal.connecting ? "default" : "pointer", fontSize: 13.5, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", opacity: gcal.connecting ? 0.6 : 1 }}>
                        {gcal.connecting ? "Connecting…" : "Connect Google Calendar"}
                      </button>
                    )}
                    {gcal.error && (
                      <p style={{ margin: "10px 0 0", fontSize: 11, color: "#e0608a", lineHeight: 1.5 }}>{gcal.error}</p>
                    )}
                  </>
                ) : (
                  <>
                    {/* Which Google account this connection actually belongs to.
                        A primary calendar's ID is that account's email, so this
                        is the ground truth — without it there's no way to tell
                        the wrong account got connected. */}
                    {gcal.calendars.find(c => c.primary) && (
                      <div style={{ fontSize: 11.5, color: "var(--c-text-soft)", marginBottom: 10 }}>
                        Connected as <strong style={{ color: "var(--c-text)" }}>{gcal.calendars.find(c => c.primary).id}</strong>
                      </div>
                    )}

                    {/* Target calendar picker */}
                    <label style={{ fontSize: 11, fontWeight: 700, color: "var(--c-text-soft)", display: "block", marginBottom: 6 }}>Sync to Google Calendar</label>
                    <select value={gcal.calendarId} onChange={(e) => gcal.setCalendarId(e.target.value)}
                      style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid var(--c-border)", fontSize: 13, background: "var(--c-bg-card)", color: "var(--c-text)", marginBottom: 12 }}>
                      {gcal.calendars.length === 0 && <option value={gcal.calendarId}>Primary</option>}
                      {gcal.calendars.map(c => <option key={c.id} value={c.id}>{c.summary}{c.primary ? " (primary)" : ""}</option>)}
                    </select>

                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={onSyncNow} disabled={gcal.status === "syncing"}
                        style={{ flex: 1, padding: "10px", borderRadius: 11, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", opacity: gcal.status === "syncing" ? 0.6 : 1 }}>
                        {gcal.status === "syncing" ? "Syncing…" : "Sync now"}
                      </button>
                      <button onClick={gcal.disconnect}
                        style={{ padding: "10px 14px", borderRadius: 11, border: "none", boxShadow: OPTION_SHADOW, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-card)" }}>
                        Disconnect
                      </button>
                    </div>

                    {/* Status line */}
                    <p style={{ margin: "10px 0 0", fontSize: 11, color: gcal.status === "error" ? "#e0608a" : "var(--c-text-soft)", lineHeight: 1.5 }}>
                      {gcal.status === "error"
                        ? (gcal.error || "Sync failed. Will retry.")
                        : gcal.lastSyncAt
                          ? `Last synced ${new Date(gcal.lastSyncAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` +
                            (gcal.lastStats ? ` · ↓${gcal.lastStats.imported + gcal.lastStats.updated} ↑${gcal.lastStats.pushed} ✕${gcal.lastStats.deleted}` : "")
                          : "Syncing automatically when events change."}
                    </p>

                    {/* Task list picker + sync (non-recurring Tasks only — Google
                        Tasks has no recurrence concept). Paused for now — see
                        isGoogleTasksConfigured in src/google/calendar.js. */}
                    {gcal.tasksConfigured && (
                    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--c-border-light)" }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: "var(--c-text-soft)", display: "block", marginBottom: 6 }}>Sync to Google Tasks</label>
                      {gcal.taskLists.length === 0 ? (
                        <p style={{ margin: "0 0 12px", fontSize: 11.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>
                          Task sync needs one more permission. Tap Disconnect above, then Connect again to grant it.
                        </p>
                      ) : (
                        <>
                          <select value={gcal.taskListId} onChange={(e) => gcal.setTaskListId(e.target.value)}
                            style={{ width: "100%", padding: "9px 10px", borderRadius: 10, border: "1px solid var(--c-border)", fontSize: 13, background: "var(--c-bg-card)", color: "var(--c-text)", marginBottom: 12 }}>
                            {gcal.taskLists.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                          </select>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={onSyncTasksNow} disabled={gcal.taskStatus === "syncing"}
                              style={{ flex: 1, padding: "10px", borderRadius: 11, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", opacity: gcal.taskStatus === "syncing" ? 0.6 : 1 }}>
                              {gcal.taskStatus === "syncing" ? "Syncing…" : "Sync tasks now"}
                            </button>
                            <button onClick={gcal.disconnectTasks}
                              style={{ padding: "10px 14px", borderRadius: 11, border: "none", boxShadow: OPTION_SHADOW, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--c-text-mid)", background: "var(--c-bg-card)" }}>
                              Disconnect
                            </button>
                          </div>
                          <p style={{ margin: "10px 0 0", fontSize: 11, color: gcal.taskStatus === "error" ? "#e0608a" : "var(--c-text-soft)", lineHeight: 1.5 }}>
                            {gcal.taskStatus === "error"
                              ? (gcal.taskError || "Task sync failed. Will retry.")
                              : gcal.lastTaskSyncAt
                                ? `Last synced ${new Date(gcal.lastTaskSyncAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` +
                                  (gcal.lastTaskStats ? ` · ↓${gcal.lastTaskStats.imported + gcal.lastTaskStats.updated} ↑${gcal.lastTaskStats.pushed} ✕${gcal.lastTaskStats.deleted}` : "")
                                : "Only one-time tasks sync. Repeating tasks stay local (Google Tasks has no repeat option)."}
                          </p>
                        </>
                      )}
                    </div>
                    )}
                  </>
                )}
                <p style={{ margin: "12px 0 0", fontSize: 10.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>
                  By connecting, you agree to our{" "}
                  <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>Terms</a>
                  {" "}and{" "}
                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>Privacy Policy</a>.
                </p>
              </div>
            )}
          </>
        )}

        {/* Start of Week */}
        <div onClick={() => toggleSub("week")} style={{ ...rowStyle, background: "var(--c-bg-card)" }}>
          <span style={labelStyle}>Start of Week</span>
          <span style={{ fontSize: 12, color: "var(--c-text-soft)", textTransform: "capitalize" }}>{weekStart}</span>
          <span style={{ ...chevStyle, transform: plannerSubOpen === "week" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {plannerSubOpen === "week" && (
          <div style={{ padding: "10px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {["sunday","monday"].map(w => (
                <button key={w} onClick={() => saveWeek(w)}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 10, ...optionButton(weekStart === w), fontFamily: "inherit", fontSize: 13, cursor: "pointer", textTransform: "capitalize" }}>{w}</button>
              ))}
            </div>
          </div>
        )}

        {/* Calendar Grid — a white card, or no surface so only the grid lines
            show over the background (Jade, 2026-10-15). */}
        <div onClick={() => toggleSub("calGrid")} style={{ ...rowStyle, background: "var(--c-bg-card)" }}>
          <span style={labelStyle}>Calendar Grid</span>
          <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}>{calGridSurface === "match" ? "Match background" : "White"}</span>
          <span style={{ ...chevStyle, transform: plannerSubOpen === "calGrid" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {plannerSubOpen === "calGrid" && (
          <div style={{ padding: "10px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {[["white", "White", 0.7], ["match", "Match background", 1.3]].map(([v, label, grow]) => (
                <button key={v} onClick={() => onCalGridSurfaceChange(v)}
                  style={{ flex: grow, padding: "9px 4px", borderRadius: 10, ...optionButton(calGridSurface === v), fontFamily: "inherit", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Day Starts At — which hour the Planner's Week timeline grid shows
            as its first row. Most people don't want their calendar grid to
            open at midnight; this lets it open at whatever hour they
            actually start their day (e.g. when they usually wake up). */}
        <div onClick={() => toggleSub("dayStart")} style={{ ...rowStyle, background: "var(--c-bg-card)" }}>
          <span style={labelStyle}>Day Starts At</span>
          <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}>{fmtStartHour(dayStartHour)}</span>
          <span style={{ ...chevStyle, transform: plannerSubOpen === "dayStart" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {plannerSubOpen === "dayStart" && (
          <div style={{ padding: "10px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
            <select value={dayStartHour} onChange={e => onDayStartHourChange(Number(e.target.value))}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 10, border: "none", boxShadow: OPTION_SHADOW, background: "var(--c-bg-card)", color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{fmtStartHour(h)}</option>
              ))}
            </select>
          </div>
        )}

        {/* Holidays — which country's public holidays the Planner shows. */}
        <div onClick={() => toggleSub("holidays")} style={{ ...rowStyle, background: "var(--c-bg-card)" }}>
          <span style={labelStyle}>Holidays</span>
          <span style={{ fontSize: 12, color: "var(--c-text-soft)", textAlign: "right" }}>{holidaySummary}</span>
          <span style={{ ...chevStyle, transform: plannerSubOpen === "holidays" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {plannerSubOpen === "holidays" && (
          <div style={{ padding: "10px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
            <div style={{ display: "flex", gap: 8 }}>
              {[["regional", "Regional", 1], ["manual", "Pick a country", 1.4], ["off", "Off", 0.7]].map(([m, label, grow]) => {
                const on = holidayPick.mode === m;
                return (
                  <button key={m} onClick={() => pickHolidayMode(m)}
                    style={{ flex: grow, padding: "9px 0", borderRadius: 10, ...optionButton(on), fontFamily: "inherit", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>{label}</button>
                );
              })}
            </div>
            {holidayPick.mode === "manual" && (
              <select value={holidayPick.manual} onChange={e => pickHolidayCountry(e.target.value)}
                style={{ marginTop: 10, width: "100%", padding: "8px 10px", borderRadius: 10, border: "none", boxShadow: OPTION_SHADOW, background: "var(--c-bg-card)", color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {holidayCountryOptions.map(({ cc, name }) => (
                  <option key={cc} value={cc}>{name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        </div>
        )}

        {/* Alarm Snooze — how long the Snooze button on a ringing alarm waits.
            Its own top-level row (Android only; showSnoozeSetting is also on
            for the web while the design is being reviewed). */}
        {showSnoozeSetting && (
          <>
            <div onClick={() => toggle("snooze")} style={rowStyle}>
              <AlarmClock size={18} color="var(--c-text-accent)" />
              <span style={labelStyle}>Alarm Snooze</span>
              <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}>{snoozeMinutes} min</span>
              <span style={{ ...chevStyle, transform: openSetting === "snooze" ? "rotate(90deg)" : "none" }}>▸</span>
            </div>
            {openSetting === "snooze" && (
              <div style={{ padding: "12px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  {SNOOZE_OPTIONS.map(m => {
                    const on = snoozeMinutes === m;
                    return (
                      <button key={m} onClick={() => pickSnooze(m)}
                        style={{ flex: 1, padding: "9px 0", borderRadius: 10, ...optionButton(on), fontFamily: "inherit", fontSize: 13, cursor: "pointer" }}>
                        {m} min
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* App Blocker — real blocklist picker + per-session toggle (set from
            Clock-In). Its own top-level row; `appBlockerRowRef`/the pulse class
            are for the "Open Settings" deep link from Clock-In, which opens this
            row, scrolls there and flashes once. */}
        {appBlockerIsNative && (
          <div ref={appBlockerRowRef} className={pulseAppBlockerRow ? "settings-row-pulse" : undefined}>
            {/* Free accounts get a locked, non-tappable row (no dropdown) — same
                "lock, no redirect" rule as the other Premium spots. While the
                plan is still loading it behaves like the open row, and
                AppBlockerSection shows its Loading dots until it knows. */}
            <div onClick={appBlockerLocked ? undefined : () => toggle("appBlocker")} style={appBlockerLocked ? { ...rowStyle, cursor: "default" } : rowStyle}>
              <Shield size={18} color="var(--c-text-accent)" />
              <span style={labelStyle}>Clock-In App Blocker</span>
              {appBlockerLocked
                ? <LockBadge size={18} />
                : <span style={{ ...chevStyle, transform: openSetting === "appBlocker" ? "rotate(90deg)" : "none" }}>▸</span>}
            </div>
            {openSetting === "appBlocker" && !appBlockerLocked && (
              <div style={{ padding: "14px 20px 16px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
                <AppBlockerSection />
              </div>
            )}
          </div>
        )}

        {/* Widget Color — one colour for every home-screen widget: the app's own
            theme, or one of the category colours (light pastel background,
            vibrant accent). Android-only; see capacitor/widgetColor.js. */}
        {showWidgetColorSetting && (
          <>
            <div onClick={() => toggle("widgetColor")} style={rowStyle}>
              <Palette size={18} color="var(--c-text-accent)" />
              <span style={labelStyle}>Widget color</span>
              {widgetColor === WIDGET_COLOR_THEME || widgetColor === WIDGET_COLOR_WHITE || widgetColor === WIDGET_COLOR_BLACK
                ? <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}>{widgetColor === WIDGET_COLOR_WHITE ? "White" : widgetColor === WIDGET_COLOR_BLACK ? "Black" : "Background"}</span>
                : <span style={{ width: 16, height: 16, borderRadius: "50%", background: pastelWidgetTheme(widgetColor).accent, flexShrink: 0 }} />}
              <span style={{ ...chevStyle, transform: openSetting === "widgetColor" ? "rotate(90deg)" : "none" }}>▸</span>
            </div>
            {openSetting === "widgetColor" && (
              <div style={{ padding: "12px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  {[[WIDGET_COLOR_THEME, "Match background"], [WIDGET_COLOR_WHITE, "White"], [WIDGET_COLOR_BLACK, "Black"]].map(([val, label]) => {
                    const on = widgetColor === val;
                    return (
                      <button key={val} onClick={() => pickWidgetColor(val)} className={val === WIDGET_COLOR_WHITE ? "keep-white" : undefined}
                        style={{ flex: val === WIDGET_COLOR_THEME ? 1.6 : 1, padding: "9px 0", borderRadius: 10, ...optionButton(on),
                          // White / Black show their own colour; the picked one gets a ring in the
                          // accent drawn with the shadow (no border line), since a tint can't show on them.
                          ...(val === WIDGET_COLOR_WHITE || val === WIDGET_COLOR_BLACK ? { background: val === WIDGET_COLOR_WHITE ? "#ffffff" : "#1c1c1e", color: val === WIDGET_COLOR_BLACK ? "#f2f2f7" : on ? "color-mix(in srgb, var(--c-primary) 72%, #000)" : "#4a4048", boxShadow: on ? `0 0 0 2px var(--c-primary), ${OPTION_SHADOW}` : OPTION_SHADOW } : {}),
                          fontFamily: "inherit", fontSize: 13, cursor: "pointer" }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, justifyItems: "center" }}>
                  {WIDGET_COLOR_OPTIONS.map(c => {
                    const on = widgetColor === c;
                    const t = pastelWidgetTheme(c);
                    return (
                      <button key={c} onClick={() => pickWidgetColor(c)} aria-label={`Widget color ${c}`}
                        style={{ width: 31, height: 31, borderRadius: "50%", padding: 0, cursor: "pointer", background: t.bg, border: `2px solid ${on ? t.accent : "transparent"}`, boxShadow: "0 1px 3px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ width: 13, height: 13, borderRadius: "50%", background: t.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {on && <Check size={10} color="#fff" strokeWidth={3} />}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* How to add widgets — just the label, no border or arrow; tapping it
                    shows/hides the steps. */}
                <button onClick={() => setWidgetHelpOpen(v => !v)}
                  style={{ marginTop: 14, padding: 0, border: "none", background: "none", color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
                  How to add widgets?
                </button>
                {widgetHelpOpen && (
                  <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 10, background: "var(--c-bg-card)", fontSize: 12.5, lineHeight: 1.6, color: "var(--c-text)" }}>
                    <ol style={{ margin: 0, paddingLeft: 18 }}>
                      <li>Press and hold an empty spot on your home screen.</li>
                      <li>Tap <b>Widgets</b>.</li>
                      <li>Find <b>Clarity</b> in the list.</li>
                      <li>Press and hold a widget, drag it where you want it, then let go.</li>
                    </ol>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Tabs — choose which tabs show in the nav (data is preserved when hidden) */}
        <div onClick={() => toggle("tabs")} style={rowStyle}>
          <LayoutGrid size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Tabs</span>
          <span style={{ ...chevStyle, transform: openSetting === "tabs" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "tabs" && (
          <div style={{ padding: "6px 20px 14px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
            {tabOptions.map(t => {
              const on = enabledTabs[t.id] !== false;
              const Icon = t.icon;
              return (
                <div key={t.id} onClick={() => onToggleTab(t.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", cursor: "pointer" }}>
                  <Icon size={17} color="var(--c-text-accent)" />
                  <span style={{ flex: 1, fontSize: 13, color: "var(--c-text)", fontWeight: 500 }}>{t.label}</span>
                  <div style={{ width: 38, height: 22, borderRadius: 999, background: on ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-border)", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
                    <div style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "var(--c-bg-card)", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s cubic-bezier(0.34,1.56,0.64,1)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Background (was "App Theme" — the gradients only change the canvas behind the cards) — one container; tap a palette to switch (mutually exclusive) */}
        <div onClick={() => toggle("appTheme")} style={rowStyle}>
          <Sparkles size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Background</span>
          <span style={{ fontSize: 12, color: "var(--c-text-soft)" }}>{darkActive ? "Dark" : (APP_THEME_OPTIONS.find(o => o.id === bgTheme) || APP_THEME_OPTIONS[0]).label}</span>
          <span style={{ ...chevStyle, transform: openSetting === "appTheme" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "appTheme" && (() => {
          // Default sits on its own full-width row up top with its accent picker directly
          // beneath it (it's the only theme with a user-pickable accent, since it has no
          // gradient of its own); every other theme follows in the 2-column grid below.
          // Dark mode (Jade, 2026-10-17): Dark sits beside Default and shares its
          // accent picker. (Black and "Match phone" were removed the same day.)
          const DARK_OPTS = [
            { id: "dark", label: "Dark", preview: "#121212", chip: "#2a2a2a" },
          ];
          const themeCard = (opt, full = false) => {
            const isDarkOpt = opt.id === "dark";
            const selected = isDarkOpt ? darkActive : (!darkActive && bgTheme === opt.id);
            const locked = Boolean(opt.premium) && !isPremium;
            return (
              <button key={opt.id} onClick={() => { if (locked) return; if (isDarkOpt) onDarkSelect(); else onThemeSelect(opt.id); }} title={locked ? `${opt.label} (Premium)` : opt.label}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "stretch", gap: 8, padding: 8, borderRadius: 14, cursor: "pointer",
                  background: "var(--c-bg-card)",
                  border: selected ? "2px solid var(--c-primary)" : "2px solid var(--c-border-light)",
                  boxShadow: selected ? "0 4px 14px var(--c-shadow)" : "none",
                  transition: "border-color 0.18s, box-shadow 0.18s, transform 0.18s",
                  width: full ? "100%" : undefined, boxSizing: "border-box",
                  opacity: locked ? 0.82 : 1,
                }}>
                {/* Mini preview: the canvas gradient + a small "card" chip riding on it */}
                <span className={opt.id === "none" ? "keep-white" : undefined} style={{ position: "relative", height: full ? 40 : 46, borderRadius: 10, background: opt.preview, boxShadow: isDarkOpt ? "inset 0 0 0 1px rgba(255,255,255,0.10)" : "inset 0 0 0 1px rgba(0,0,0,0.06)", overflow: "hidden" }}>
                  <span className="keep-white" style={{ position: "absolute", left: 8, bottom: 8, width: 24, height: 15, borderRadius: 4, background: opt.chip, boxShadow: "0 1px 3px rgba(0,0,0,0.22)" }} />
                  {locked && (
                    <span style={{ position: "absolute", top: 6, right: 6 }}>
                      <LockBadge size={20} />
                    </span>
                  )}
                </span>
                <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: selected ? "var(--c-text-accent)" : "var(--c-text)" }}>{opt.label}</span>
                  {selected && <Check size={14} color="var(--c-primary)" strokeWidth={3} />}
                </span>
              </button>
            );
          };
          const defaultOpt = APP_THEME_OPTIONS.find(o => o.id === "none");
          const restOpts = APP_THEME_OPTIONS.filter(o => o.id !== "none");
          return (
          <div style={{ padding: "14px 20px 16px", background: "var(--c-bg-soft)", borderBottom: "1px solid var(--c-border-light)" }}>
            {/* Default and Dark in one row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {defaultOpt && themeCard(defaultOpt)}
              {DARK_OPTS.map(o => themeCard(o))}
            </div>

            {/* Accent Color picker: shared by Default and Dark */}
            {(bgTheme === "none" || darkActive) && (
              <div style={{ marginTop: 12, padding: "12px 14px", background: "var(--c-bg-card)", borderRadius: 14 }}>
                <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 800, letterSpacing: 0.3, color: "var(--c-text-soft)", textTransform: "uppercase" }}>Accent Color</p>
                {/* CSS grid (not flex-wrap) so every row's swatches land on the same
                    column x-positions and the last swatch in each row reaches the
                    container's right edge instead of leaving a ragged gap. */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(30px, 1fr))", justifyItems: "center", gap: 8 }}>
                  {PICKER_PRESETS.map(hex => {
                    const selected = defaultAccent?.toLowerCase() === hex.toLowerCase();
                    return (
                      <button key={hex} onClick={() => onDefaultAccentSelect(hex)} title={hex}
                        style={{
                          width: 30, height: 30, borderRadius: "50%", background: hex, cursor: "pointer",
                          border: selected ? "2.5px solid var(--c-text)" : "2px solid rgba(0,0,0,0.08)",
                          boxShadow: selected ? "0 2px 8px rgba(0,0,0,0.18)" : "none",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                        {selected && <Check size={14} color="#2a2030" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Every other theme — 2-column grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              {restOpts.map(opt => themeCard(opt))}
            </div>
          </div>
          );
        })()}

        {/* Replay Guide — re-runs the welcome quiz and every coach-mark tour
            (main + per-tab + the Habit Stats scroll coach marks) from
            scratch, for anyone who wants a refresher or skipped past it
            originally. */}
        {/* Row hidden again (Jade, 2026-10-11); the confirm flow below is kept so it can come back. */}
        {false && (
        <div onClick={() => setReplayConfirm(true)} style={rowStyle}>
          <HelpCircle size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Replay Guide</span>
        </div>
        )}
        {replayConfirm && (
          <Modal onClose={() => setReplayConfirm(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "min(340px, 88vw)", background: "var(--c-bg-card)", borderRadius: 20, padding: "26px 22px 20px", textAlign: "center", boxShadow: "0 12px 40px rgba(0,0,0,0.25)" }}>
              <ConfirmHeader tone="accent" icon={HelpCircle} title="Replay the welcome guide?" subtitle="This restarts the welcome quiz and every tour. Your data isn't affected." />
              <div style={CONFIRM_STACK}>
                <button onClick={() => setReplayConfirm(false)} style={CONFIRM_CANCEL}>Cancel</button>
                <button onClick={() => { setReplayConfirm(false); onReplayOnboarding(); }} style={CONFIRM_PRIMARY}>Replay</button>
              </div>
            </div>
          </Modal>
        )}

        {/* FAQs — two groups: using the app, then payments (Jade, 2026-10-11). */}
        <div onClick={() => toggle("faqs")} style={rowStyle}>
          <FileQuestion size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>FAQs</span>
          <span style={{ ...chevStyle, transform: openSetting === "faqs" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "faqs" && (
          <div style={{ padding: "6px 20px 16px", background: "var(--c-bg-card)", borderBottom: "1px solid var(--c-settings-line, #f5eaf0)", textAlign: "left" }}>
            <div style={{ margin: "8px 0 2px", fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--c-text-accent)" }}>Using Clarity</div>
            <FaqList items={appFaqs(isNativeAndroid())} group="app" openKey={openFaq} onToggle={setOpenFaq} />
            <div style={{ margin: "16px 0 2px", fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--c-text-accent)" }}>Payments & Premium</div>
            <FaqList items={paymentFaqs} group="pay" openKey={openFaq} onToggle={setOpenFaq} />
          </div>
        )}

        {/* Feedback + Follow Us rows hidden for now (Jade, 2026-10-26); both
            were only "coming soon" placeholders. Drop the `false &&` to bring them back. */}
        {false && (<>
        {/* Feedback */}
        <div onClick={() => toggle("feedback")} style={rowStyle}>
          <MessageCircle size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Feedback</span>
          <span style={{ ...chevStyle, transform: openSetting === "feedback" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "feedback" && (
          <div style={{ padding: "10px 20px 14px", background: "var(--c-bg-card)", borderBottom: "1px solid var(--c-settings-line, #f5eaf0)", textAlign: "left" }}>
            <p style={{ margin: 0, fontSize: 13, color: "var(--c-text-soft)" }}>Feedback form coming soon</p>
          </div>
        )}

        {/* Follow Us */}
        <div onClick={() => toggle("follow")} style={rowStyle}>
          <UserPlus size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Follow Us</span>
          <span style={{ ...chevStyle, transform: openSetting === "follow" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "follow" && (
          <div style={{ padding: "10px 20px 14px", background: "var(--c-bg-card)", borderBottom: "1px solid var(--c-settings-line, #f5eaf0)", textAlign: "left" }}>
            <p style={{ margin: 0, fontSize: 13, color: "var(--c-text-soft)" }}>Links coming soon</p>
          </div>
        )}
        </>)}

        {/* Credits — tucked behind a collapsed row instead of sitting as a
            permanently visible line at the bottom of the screen. Zapsplat's
            Standard License just asks that the credit be reasonably
            reachable somewhere in the app (see CREDITS.md), not that it be
            unmissable, so a tap-to-expand row still satisfies it. */}
        <div onClick={() => toggle("credits")} style={rowStyle}>
          <Info size={18} color="var(--c-text-accent)" />
          <span style={labelStyle}>Credits</span>
          <span style={{ ...chevStyle, transform: openSetting === "credits" ? "rotate(90deg)" : "none" }}>▸</span>
        </div>
        {openSetting === "credits" && (
          <div style={{ padding: "10px 20px 32px", background: "var(--c-bg-card)", borderBottom: "1px solid var(--c-settings-line, #f5eaf0)", textAlign: "left" }}>
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>
              Notification sound effect from{" "}
              <a href="https://www.zapsplat.com" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                zapsplat.com
              </a>
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>
              Onboarding illustrations by Storyset:{" "}
              <a href="https://storyset.com/health" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                Health
              </a>,{" "}
              <a href="https://storyset.com/work" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                Work
              </a>,{" "}
              <a href="https://storyset.com/data" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                Data
              </a>,{" "}
              <a href="https://storyset.com/happy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                Happy
              </a>,{" "}
              <a href="https://storyset.com/people" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                People
              </a>, and{" "}
              <a href="https://storyset.com/online" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                Online
              </a>{" "}illustrations
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--c-text-soft)", lineHeight: 1.5 }}>
              Public holiday dates from{" "}
              <a href="https://github.com/commenthol/date-holidays" target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-primary-deep)" }}>
                date-holidays
              </a>{" "}(CC BY 3.0)
            </p>
          </div>
        )}

      </>
      </div>
    </div>
  );
}

